import { useEffect, useState } from "react";
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { verifyAdmin } from "@/lib/admin/authorization.functions";
import { AdminShell } from "@/components/admin/AdminShell";
import { Toaster } from "@/components/ui/sonner";

/**
 * Pathless layout for the admin surface.
 *
 * ssr:false because the Supabase session lives in localStorage — the server
 * cannot read it during SSR/prerender. This guard is a **client-side
 * navigation / UX guard only**; it exists to keep unsigned or non-admin
 * visitors out of the admin UI and to avoid rendering shells the user isn't
 * allowed to see.
 *
 * The real security boundaries live on the server:
 *   1. requireSupabaseAuth middleware on every admin server fn (401 without a
 *      valid bearer token).
 *   2. has_role('admin') re-check inside every admin handler (forbidden for
 *      non-admins).
 *   3. Admin-only RLS SELECT policies on contact_enquiries,
 *      newsletter_subscribers, delivery_area_requests — non-admins get
 *      zero rows even if a handler forgets the has_role check.
 *   4. No private data in route code or initial HTML — data fetches happen
 *      via server fns after the client-side auth check.
 *
 * Bypassing this UX guard (e.g. calling server fns from DevTools with a
 * non-admin bearer) still fails at layers 1–3.
 */
export const Route = createFileRoute("/_admin")({
  head: () => ({
    meta: [
      { title: "Admin — Poonam Kapur Healthy Kitchen" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  ssr: false,
  component: AdminGate,
});

type GateState =
  | { status: "checking" }
  | { status: "ready" }
  | { status: "redirecting" };

function AdminGate() {
  const navigate = useNavigate();
  const verify = useServerFn(verifyAdmin);
  const [state, setState] = useState<GateState>({ status: "checking" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Only call the protected server check once a signed-in session exists,
      // so signed-out visitors never send an unauthenticated request.
      const [{ data }, { data: sessionData }] = await Promise.all([
        supabase.auth.getUser(),
        supabase.auth.getSession(),
      ]);
      if (cancelled) return;
      if (!data.user || !sessionData.session?.access_token) {
        setState({ status: "redirecting" });
        navigate({ to: "/admin/login", replace: true });
        return;
      }

      const verifyPromise = verify().then(
        (res) => ({ ok: true as const, res }),
        (error) => ({ ok: false as const, error }),
      );
      const verified = await verifyPromise;
      if (cancelled) return;
      if (!verified.ok) {
        setState({ status: "redirecting" });
        navigate({ to: "/admin/login", replace: true });
        return;
      }
      if (!verified.res.isAdmin) {
        await supabase.auth.signOut();
        if (cancelled) return;
        setState({ status: "redirecting" });
        navigate({
          to: "/admin/login",
          replace: true,
          search: { error: "not_admin" },
        });
        return;
      }
      setState({ status: "ready" });
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, verify]);


  if (state.status !== "ready") {
    return (
      <div
        className="flex min-h-dvh items-center justify-center bg-background text-sm text-muted-foreground"
        role="status"
        aria-live="polite"
      >
        Checking access…
      </div>
    );
  }

  return (
    <AdminShell>
      <Outlet />
      <Toaster richColors position="top-right" />
    </AdminShell>
  );
}